import { Box, Popover } from '@mui/material';
import { History } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import { RedoIcon, UndoIcon } from '~/assets/workspace-icons';
import { editBlockedMessageId } from '~/components/Structures/editBlockedMessage';
import ToolbarButton from '~/components/toolbar/shared/ToolbarButton';
import { useStructureToolGating } from '~/hooks/target/useStructureToolGating';
import {
    useContourUndoRedoShortcuts,
    type UndoRedoShortcut,
} from '~/hooks/undo/useContourUndoRedoShortcuts';
import { useCrossSectionDrawingHistory } from '~/hooks/undo/useCrossSectionDrawingHistory';
import { useUndoRedo } from '~/hooks/undo/useUndoRedo';
import { currentStructurePeekState } from '~/state/atoms/structureSet/roiAtoms';
import StructureHistoryPanel from './StructureHistoryPanel';

/**
 * How long the acknowledging highlight stays on. Long enough to catch in
 * peripheral vision, short enough that a second press reads as a second
 * action rather than one continuous state.
 */
const FLASH_MS = 300;

/**
 * Undo, Redo, and the structure edit history for the selected ROI.
 *
 * Mounted once, on the toolbar. A second instance anywhere would have to
 * suppress its own shortcut binding — {@link useContourUndoRedoShortcuts}
 * registers a WINDOW key listener, so two bindings step the same undo
 * stack twice per Ctrl+Z.
 */
export default function UndoRedoTool() {
    const intl = useIntl();
    const {
        selectedStructureSet,
        selectedRoiNumber,
        structureSetEditDisabled,
        selectionEditBlocker: editBlocker,
    } = useStructureToolGating();
    const {
        canUndo: canUndoHistory,
        canRedo: canRedoHistory,
        canRevertToInitial,
        history,
        discardedStepCount,
        undo,
        redo,
        jumpTo,
        revertToInitial,
    } = useUndoRedo(selectedStructureSet?.dicomUid, selectedRoiNumber);

    // Unapplied cross-sections are the newest edit, so they are stepped back first.
    const drawing = useCrossSectionDrawingHistory();
    const canUndo = drawing.canUndo || canUndoHistory;
    const canRedo = drawing.canRedo || canRedoHistory;

    // Which stack each toolbar Undo stepped back, so Redo mirrors it; scoped to the structure set.
    const undoLog = useRef<('drawing' | 'history')[]>([]);
    const structureSetUid = selectedStructureSet?.dicomUid;
    useEffect(() => {
        undoLog.current = [];
    }, [structureSetUid]);

    const handleUndo = (): boolean => {
        const stepped = drawing.canUndo
            ? drawing.undo()
            : canUndoHistory && Boolean(undo());
        if (stepped) {
            undoLog.current.push(drawing.canUndo ? 'drawing' : 'history');
        }
        return stepped;
    };

    const handleRedo = (): boolean => {
        if (undoLog.current.pop() === 'drawing' && drawing.canRedo) {
            return drawing.redo();
        }
        if (canRedoHistory) {
            return redo() !== undefined;
        }
        return drawing.canRedo && drawing.redo();
    };

    const historyAnchorRef = useRef<HTMLElement | null>(null);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [flash, setFlash] = useState<UndoRedoShortcut | null>(null);
    const flashTimer = useRef<ReturnType<typeof setTimeout>>();

    /**
     * A shortcut gives no feedback of its own: the contour changes somewhere
     * the user may not be looking, and nothing says which of the two keys was
     * understood. Flashing the matching button answers both.
     */
    const acknowledge = (action: UndoRedoShortcut) => {
        setFlash(action);
        clearTimeout(flashTimer.current);
        flashTimer.current = setTimeout(() => setFlash(null), FLASH_MS);
    };

    useEffect(() => () => clearTimeout(flashTimer.current), []);

    // An undo of a peeked structure would change a contour the views are
    // not drawing.
    const peeked =
        useRecoilValue(currentStructurePeekState)?.roiNumber ===
        selectedRoiNumber;
    const editingDisabled =
        structureSetEditDisabled || editBlocker !== undefined || peeked;

    // Bound here rather than globally so the keys are live exactly while these
    // buttons are, under exactly their gating.
    useContourUndoRedoShortcuts({
        enabled: !editingDisabled,
        canUndo,
        canRedo,
        // Acknowledge only once a step was really restored, never a press
        // that did nothing.
        undo: () => {
            if (handleUndo()) {
                acknowledge('undo');
            }
        },
        redo: () => {
            if (handleRedo()) {
                acknowledge('redo');
            }
        },
    });

    /**
     * A blocking guard outranks availability: a user looking at a protected
     * structure needs to know that is why the control is dead, not that there
     * happens to be nothing to undo.
     */
    const tooltip = (
        available: boolean,
        availableId: string,
        emptyId: string
    ): string =>
        intl.formatMessage({
            id: editBlocker
                ? editBlockedMessageId(editBlocker)
                : available
                  ? availableId
                  : emptyId,
        });

    /**
     * No confirmation, however far back the step is: the list names each step
     * and when it was recorded, so the choice is read before it is made, and
     * the jump stays reversible until the next edit.
     */
    const restoreStep = (index: number) => {
        setHistoryOpen(false);
        jumpTo(index);
    };

    const restoreInitial = () => {
        setHistoryOpen(false);
        revertToInitial();
    };

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.25,
            }}
        >
            <ToolbarButton
                icon={<UndoIcon />}
                tooltip={tooltip(
                    canUndo,
                    'toolbar.undo.tooltip',
                    'toolbar.undo.nothing'
                )}
                flashing={flash === 'undo'}
                disabled={!canUndo || editingDisabled}
                onClick={handleUndo}
                data-testid="tool-undo"
            />
            <ToolbarButton
                icon={<RedoIcon />}
                tooltip={tooltip(
                    canRedo,
                    'toolbar.redo.tooltip',
                    'toolbar.redo.nothing'
                )}
                flashing={flash === 'redo'}
                disabled={!canRedo || editingDisabled}
                onClick={handleRedo}
                data-testid="tool-redo"
            />
            <ToolbarButton
                icon={<History />}
                tooltip={tooltip(
                    history.length > 0,
                    'toolbar.history.tooltip',
                    'toolbar.history.empty'
                )}
                selected={historyOpen}
                disabled={history.length === 0 || editingDisabled}
                anchorRef={historyAnchorRef}
                onClick={() => setHistoryOpen((previous) => !previous)}
                data-testid="tool-structure-history"
            />
            <Popover
                open={historyOpen}
                anchorEl={historyAnchorRef.current}
                onClose={() => setHistoryOpen(false)}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
                slotProps={{
                    paper: {
                        sx: (theme) => ({
                            ...theme.mixins.floatingSurface,
                            mt: 1,
                            py: 1,
                            // glassOverlay is a dark translucent panel but sets
                            // no text colour, so the list would otherwise
                            // inherit the toolbar's and read washed out
                            // against it. Same treatment as the difference
                            // overlay popover.
                            color: 'rgba(255, 255, 255, 0.95)',
                        }),
                    },
                }}
            >
                <StructureHistoryPanel
                    history={history}
                    discardedStepCount={discardedStepCount}
                    canRevertToInitial={canRevertToInitial}
                    disabled={editingDisabled}
                    onSelect={restoreStep}
                    onRevertToInitial={restoreInitial}
                />
            </Popover>
        </Box>
    );
}
